export const CREATE_SITE = /* GraphQL */ `
  mutation CreateSite($input: CreateSiteInput!) {
    createSite(input: $input) {
      siteId
      userId
      name
      url
      description
      status
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_SITE = /* GraphQL */ `
  mutation UpdateSite($input: UpdateSiteInput!) {
    updateSite(input: $input) {
      siteId
      userId
      name
      url
      description
      status
      updatedAt
      maxPages
      pauseAfterCrawl
      defaultScanTools
      thresholdFilters {
        minSeverity
        minIssueCount
        minScore
        hideAcceptedRisks
      }
      emailSettings {
        enabled
        recipients
        sendCondition
        includeChart
        senderEmail
        senderVerified
      }
    }
  }
`;

export const VERIFY_SENDER_EMAIL = /* GraphQL */ `
  mutation VerifySenderEmail($siteId: ID!, $email: String!) {
    verifySenderEmail(siteId: $siteId, email: $email) {
      success
      email
      message
    }
  }
`;

export const CHECK_SENDER_EMAIL_VERIFICATION = /* GraphQL */ `
  mutation CheckSenderEmailVerification($siteId: ID!) {
    checkSenderEmailVerification(siteId: $siteId) {
      success
      email
      message
    }
  }
`;

export const ARCHIVE_SITE_TO_TRASH = /* GraphQL */ `
  mutation ArchiveSiteToTrash($siteId: ID!) {
    archiveSiteToTrash(siteId: $siteId) {
      siteId
      status
      trashedAt
    }
  }
`;

export const RESTORE_SITE_FROM_TRASH = /* GraphQL */ `
  mutation RestoreSiteFromTrash($siteId: ID!) {
    restoreSiteFromTrash(siteId: $siteId) {
      siteId
      status
      trashedAt
    }
  }
`;

export const PERMANENT_DELETE_SITE = /* GraphQL */ `
  mutation PermanentDeleteSite($siteId: ID!) {
    permanentDeleteSite(siteId: $siteId)
  }
`;

export const START_SCAN = /* GraphQL */ `
  mutation StartScan($input: StartScanInput!) {
    startScan(input: $input) {
      siteId
      scanId
      userId
      status
      progress {
        phase
        pagesDiscovered
        pagesScanned
        currentTool
        discoveredIssues
        stepDescription
      }
      config {
        siteUrl
        scanTypes
        wcagVersion
        wcagLevel
        pageLimit
        rateLimit
        selectedTools
        pauseAfterCrawl
      }
      startedAt
    }
  }
`;

export const CANCEL_SCAN = /* GraphQL */ `
  mutation CancelScan($siteId: ID!, $scanId: ID!) {
    cancelScan(siteId: $siteId, scanId: $scanId) {
      siteId
      scanId
      status
    }
  }
`;

export const ARCHIVE_SCAN_TO_TRASH = /* GraphQL */ `
  mutation ArchiveScanToTrash($siteId: ID!, $scanId: ID!) {
    archiveScanToTrash(siteId: $siteId, scanId: $scanId) {
      siteId
      scanId
      status
      trashedAt
    }
  }
`;

export const RESTORE_SCAN_FROM_TRASH = /* GraphQL */ `
  mutation RestoreScanFromTrash($siteId: ID!, $scanId: ID!) {
    restoreScanFromTrash(siteId: $siteId, scanId: $scanId) {
      siteId
      scanId
      status
      trashedAt
    }
  }
`;

export const PERMANENT_DELETE_SCAN = /* GraphQL */ `
  mutation PermanentDeleteScan($siteId: ID!, $scanId: ID!) {
    permanentDeleteScan(siteId: $siteId, scanId: $scanId)
  }
`;

// Team mutations
export const CREATE_TEAM = /* GraphQL */ `
  mutation CreateTeam($input: CreateTeamInput!) {
    createTeam(input: $input) {
      teamId
      name
      ownerId
      createdAt
      updatedAt
      members {
        userId
        role
        email
      }
    }
  }
`;

export const UPDATE_TEAM = /* GraphQL */ `
  mutation UpdateTeam($input: UpdateTeamInput!) {
    updateTeam(input: $input) {
      teamId
      name
      updatedAt
    }
  }
`;

export const DELETE_TEAM = /* GraphQL */ `
  mutation DeleteTeam($teamId: ID!) {
    deleteTeam(teamId: $teamId)
  }
`;

export const ADD_TEAM_MEMBER = /* GraphQL */ `
  mutation AddTeamMember($input: AddTeamMemberInput!) {
    addTeamMember(input: $input) {
      teamId
      userId
      role
      email
      joinedAt
      invitedBy
    }
  }
`;

export const REMOVE_TEAM_MEMBER = /* GraphQL */ `
  mutation RemoveTeamMember($teamId: ID!, $userId: ID!) {
    removeTeamMember(teamId: $teamId, userId: $userId)
  }
`;

// User mutations
export const UPDATE_USER_ROLE = /* GraphQL */ `
  mutation UpdateUserRole($userId: ID!, $role: UserRole!) {
    updateUserRole(userId: $userId, role: $role) {
      userId
      email
      role
      updatedAt
    }
  }
`;

export const SET_USER_DEFAULT_TEAM = /* GraphQL */ `
  mutation SetUserDefaultTeam($teamId: ID) {
    setUserDefaultTeam(teamId: $teamId) {
      userId
      defaultTeamId
      updatedAt
    }
  }
`;

// Schedule mutations
export const CREATE_SCHEDULE = /* GraphQL */ `
  mutation CreateSchedule($input: CreateScheduleInput!) {
    createSchedule(input: $input) {
      scheduleId
      siteId
      userId
      frequency
      nextRunAt
      dayOfWeek
      dayOfMonth
      hour
      minute
      timezone
      enabled
      config {
        scanTypes
        wcagVersion
        wcagLevel
        pageLimit
        rateLimit
        selectedTools
        pauseAfterCrawl
      }
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_SCHEDULE = /* GraphQL */ `
  mutation UpdateSchedule($input: UpdateScheduleInput!) {
    updateSchedule(input: $input) {
      scheduleId
      siteId
      userId
      frequency
      nextRunAt
      dayOfWeek
      dayOfMonth
      hour
      minute
      timezone
      enabled
      config {
        scanTypes
        wcagVersion
        wcagLevel
        pageLimit
        rateLimit
        selectedTools
        pauseAfterCrawl
      }
      updatedAt
    }
  }
`;

export const DELETE_SCHEDULE = /* GraphQL */ `
  mutation DeleteSchedule($scheduleId: ID!) {
    deleteSchedule(scheduleId: $scheduleId)
  }
`;

// Enabling/disabling a schedule goes through updateSchedule - there is no
// dedicated toggle mutation in the schema.
export const TOGGLE_SCHEDULE = /* GraphQL */ `
  mutation ToggleSchedule($input: UpdateScheduleInput!) {
    updateSchedule(input: $input) {
      scheduleId
      siteId
      enabled
      nextRunAt
      updatedAt
    }
  }
`;

// Paused scan mutation
export const RESUME_PAUSED_SCAN = /* GraphQL */ `
  mutation ResumePausedScan($siteId: ID!, $scanId: ID!, $proceed: Boolean!, $adjustedMaxPages: Int) {
    resumePausedScan(siteId: $siteId, scanId: $scanId, proceed: $proceed, adjustedMaxPages: $adjustedMaxPages) {
      siteId
      scanId
      userId
      status
      progress {
        phase
        pagesDiscovered
        pagesScanned
        currentPage
        estimatedTimeRemaining
        currentTool
        discoveredIssues
        stepDescription
      }
      config {
        siteUrl
        scanTypes
        wcagVersion
        wcagLevel
        pageLimit
        rateLimit
        selectedTools
        pauseAfterCrawl
      }
      startedAt
      completedAt
    }
  }
`;

// API Key mutations
export const SET_API_KEY = /* GraphQL */ `
  mutation SetApiKey($teamId: ID!, $keyType: ApiKeyType!, $apiKey: String!) {
    setApiKey(teamId: $teamId, keyType: $keyType, apiKey: $apiKey) {
      name
      keyType
      createdAt
      lastUsedAt
    }
  }
`;

export const DELETE_API_KEY = /* GraphQL */ `
  mutation DeleteApiKey($teamId: ID!, $keyType: ApiKeyType!) {
    deleteApiKey(teamId: $teamId, keyType: $keyType)
  }
`;

// Risk Acceptance mutations
/**
 * Mark one link as checked by hand for this scan only. Returns the scan so the
 * client always renders the stored list rather than its own guess at it.
 */
export const SET_LINK_REVIEWED = /* GraphQL */ `
  mutation SetLinkReviewed($input: SetLinkReviewedInput!) {
    setLinkReviewed(input: $input) {
      siteId
      scanId
      reviewedLinks
    }
  }
`;

export const ACCEPT_RISK = /* GraphQL */ `
  mutation AcceptRisk($input: AcceptRiskInput!) {
    acceptRisk(input: $input) {
      siteId
      acceptanceId
      issueId
      issueDescription
      userId
      acceptedBy
      acceptedAt
      expiresAt
      reason
      status
      pageUrls
      severity
      category
    }
  }
`;

export const EXTEND_RISK_ACCEPTANCE = /* GraphQL */ `
  mutation ExtendRiskAcceptance($input: ExtendAcceptanceInput!) {
    extendRiskAcceptance(input: $input) {
      siteId
      acceptanceId
      issueId
      issueDescription
      acceptedAt
      expiresAt
      reason
      status
    }
  }
`;

export const RESOLVE_RISK = /* GraphQL */ `
  mutation ResolveRisk($input: ResolveRiskInput!) {
    resolveRisk(input: $input) {
      siteId
      acceptanceId
      issueId
      status
      resolvedAt
      resolvedBy
      resolutionNote
    }
  }
`;

export const REVOKE_RISK_ACCEPTANCE = /* GraphQL */ `
  mutation RevokeRiskAcceptance($siteId: ID!, $acceptanceId: ID!) {
    revokeRiskAcceptance(siteId: $siteId, acceptanceId: $acceptanceId) {
      siteId
      acceptanceId
      issueId
      status
    }
  }
`;

// Time Estimate mutations (admin only)
export const UPDATE_ISSUE_TIME_ESTIMATE = /* GraphQL */ `
  mutation UpdateIssueTimeEstimate($input: UpdateIssueTimeEstimateInput!) {
    updateIssueTimeEstimate(input: $input) {
      success
      scanId
      pageUrl
      issueIdentifier
      estimatedTimeToFix
    }
  }
`;

// Google Search Console OAuth mutations
export const INITIATE_GSC_OAUTH = /* GraphQL */ `
  mutation InitiateGscOAuth($siteId: ID!, $redirectUri: String!) {
    initiateGscOAuth(siteId: $siteId, redirectUri: $redirectUri) {
      authorizationUrl
      state
    }
  }
`;

export const COMPLETE_GSC_OAUTH = /* GraphQL */ `
  mutation CompleteGscOAuth($siteId: ID!, $code: String!, $state: String!) {
    completeGscOAuth(siteId: $siteId, code: $code, state: $state) {
      success
      siteId
      propertyUrl
      message
    }
  }
`;

export const DISCONNECT_GSC = /* GraphQL */ `
  mutation DisconnectGsc($siteId: ID!) {
    disconnectGsc(siteId: $siteId)
  }
`;

export const ANALYZE_BACKLINKS = /* GraphQL */ `
  mutation AnalyzeBacklinks($siteId: ID!, $scanId: ID!) {
    analyzeBacklinks(siteId: $siteId, scanId: $scanId) {
      total
      highRisk
      mediumRisk
      lowRisk
      uniqueDomains
    }
  }
`;
